import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Check, RefreshCw } from 'lucide-react';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { textOn, initials } from './PersonColor';

// Swatches for choosing a personal colour (registration and first sign-in)
export function ColorChoiceGrid({ options = [], value, onChange, name }) {
  return (
    <div role="radiogroup" aria-label="Personal color" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
      {options.map((c) => {
        const selected = value === c.hex;
        return (
          <button
            key={c.hex}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(c.hex)}
            title={c.name}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', padding: '8px 10px',
              minWidth: '78px', borderRadius: 'var(--radius)', cursor: 'pointer', background: 'var(--white)',
              border: `2px solid ${selected ? 'var(--primary-dark)' : 'var(--border)'}`,
              boxShadow: selected ? '0 0 0 3px var(--primary-light)' : 'none',
            }}
          >
            <span
              style={{
                width: '40px', height: '40px', borderRadius: '50%', background: c.hex, color: textOn(c.hex),
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.9rem',
                boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.18)',
              }}
            >
              {selected ? <Check size={18} /> : initials(name)}
            </span>
            <span style={{ fontSize: '0.75rem', color: 'var(--text)' }}>{c.name}</span>
          </button>
        );
      })}
    </div>
  );
}

// Signed-in users without a colour (e.g. created before registration offered
// one) pick it once; afterwards only an admin can change it.
export function ColorGate() {
  const { user, updateUser } = useAuth();
  const [options, setOptions] = useState([]);
  const [value, setValue] = useState(null);
  const [saving, setSaving] = useState(false);
  const needsColor = !!user && !user.color;

  const loadOptions = () => {
    api.get('/auth/color-options').then((res) => setOptions(res.data?.options || [])).catch(() => {});
  };
  useEffect(() => { if (needsColor) loadOptions(); }, [needsColor]);

  if (!needsColor) return null;

  const save = async () => {
    if (!value) return;
    setSaving(true);
    try {
      const res = await api.put('/auth/me/color', { color: value });
      updateUser({ color: res.data.color });
      toast.success('Your color is saved.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save your color.');
      if (err.response?.data?.color_options) setOptions(err.response.data.color_options);
      setValue(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="color-gate-title"
      style={{
        position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(26,26,46,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
      }}
    >
      <div className="card" style={{ maxWidth: '440px', width: '100%' }}>
        <h2 id="color-gate-title" style={{ fontSize: '1.15rem', marginBottom: '6px' }}>Pick your color</h2>
        <p style={{ fontSize: '0.88rem', color: 'var(--text-light)', marginBottom: '16px' }}>
          Your color marks your shifts and your name everywhere in the roster, so everyone can spot you at a glance.
          You can choose it once — later only an admin can change it.
        </p>
        <ColorChoiceGrid options={options} value={value} onChange={setValue} name={user.name} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '18px', gap: '8px' }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setValue(null); loadOptions(); }} disabled={saving}>
            <RefreshCw size={13} /> Other colors
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={!value || saving}>
            {saving ? 'Saving…' : 'Save my color'}
          </button>
        </div>
      </div>
    </div>
  );
}
